import {
  BENEFICIARY_KINDS,
  BENEFICIARY_LABELS,
  type ApplyToRecordsRequest,
  type BeneficiaryKind,
  type BookkeepingRecord,
  formatMinor,
} from '@hvac/contracts';
import { Alert, Button, Card, CardBody } from '@hvac/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  applyToRecords,
  bookkeepingKeys,
  bookkeepingReferenceQuery,
  confirmManyRecords,
} from '../../shared/api/bookkeeping-queries.js';

/**
 * One decision across a run of drafts.
 *
 * A bookkeeper clearing a hundred receipts makes a dozen decisions, each
 * covering a run: these are the Doucet job, these are materials, these were
 * Marc's. Pick the field, pick the value, apply. Signed records are never in
 * the selection, and anything the database refuses comes back by name.
 */

type FieldKey =
  | 'categoryId'
  | 'vendorId'
  | 'jobId'
  | 'spentByTechnicianId'
  | 'vehicleId'
  | 'divisionId'
  | 'beneficiary';

const FIELD_LABELS: Readonly<Record<FieldKey, string>> = {
  categoryId: 'Account',
  vendorId: 'Supplier',
  jobId: 'Job',
  spentByTechnicianId: 'Who spent it',
  vehicleId: 'Vehicle',
  divisionId: 'Division',
  beneficiary: 'Who benefited',
};

const select =
  'h-9 rounded-[var(--radius-control)] border border-border-strong bg-surface px-2 text-sm';

export function BulkBar({
  organizationId,
  records,
  onDone,
  onClear,
}: {
  organizationId: string;
  records: readonly BookkeepingRecord[];
  onDone: () => void;
  onClear: () => void;
}) {
  const queryClient = useQueryClient();
  const reference = useQuery(bookkeepingReferenceQuery(organizationId));
  const [field, setField] = useState<FieldKey>('categoryId');
  const [value, setValue] = useState('');
  const [message, setMessage] = useState<{
    tone: 'success' | 'warning' | 'danger';
    text: string;
  } | null>(null);

  const ids = records.map((record) => record.id);
  const total = records.reduce((sum, record) => sum + BigInt(record.grossAmountMinor), 0n);
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: bookkeepingKeys.all(organizationId) });
  };

  const apply = useMutation({
    mutationFn: () => {
      const chosen = value === '' ? null : value;
      const patch: ApplyToRecordsRequest['patch'] =
        field === 'categoryId'
          ? { categoryId: value }
          : field === 'beneficiary'
            ? { beneficiary: value as BeneficiaryKind }
            : { [field]: chosen };
      return applyToRecords(organizationId, { recordIds: ids, patch });
    },
    onSuccess: (result) => {
      refresh();
      setMessage(
        result.skipped.length === 0
          ? {
              tone: 'success',
              text: `${FIELD_LABELS[field]} set on ${String(result.updated)} record${result.updated === 1 ? '' : 's'}.`,
            }
          : {
              tone: 'warning',
              text: `Set on ${String(result.updated)}. Skipped ${String(result.skipped.length)}: ${result.skipped.map((skip) => skip.reason).join(' ')}`,
            },
      );
    },
    onError: (caught) =>
      setMessage({
        tone: 'danger',
        text: caught instanceof Error ? caught.message : 'Nothing was changed.',
      }),
  });

  const confirm = useMutation({
    mutationFn: () => confirmManyRecords(organizationId, ids),
    onSuccess: (result) => {
      refresh();
      if (result.skipped.length === 0) {
        setMessage(null);
        onDone();
        return;
      }
      const reasons = [...new Set(result.skipped.flatMap((skip) => skip.reasons))];
      setMessage({
        tone: 'warning',
        text: `Confirmed ${String(result.signed)}. ${String(result.skipped.length)} still need a decision: ${reasons.join(' ')}`,
      });
    },
    onError: (caught) =>
      setMessage({
        tone: 'danger',
        text: caught instanceof Error ? caught.message : 'Nothing was confirmed.',
      }),
  });

  const lists = reference.data;
  const options: readonly { id: string; label: string }[] =
    lists === undefined
      ? []
      : field === 'categoryId'
        ? lists.categories.map((c) => ({
            id: c.id,
            label: c.code === null ? c.name : `${c.code} ${c.name}`,
          }))
        : field === 'vendorId'
          ? lists.vendors.map((v) => ({ id: v.id, label: v.displayName }))
          : field === 'jobId'
            ? lists.jobs.map((j) => ({
                id: j.id,
                label: j.jobNumber === null ? j.title : `${j.jobNumber}, ${j.title}`,
              }))
            : field === 'spentByTechnicianId'
              ? lists.employees.map((e) => ({ id: e.id, label: e.fullName }))
              : field === 'vehicleId'
                ? lists.vehicles.map((v) => ({
                    id: v.id,
                    label: v.plate === null ? v.name : `${v.name} (${v.plate})`,
                  }))
                : field === 'divisionId'
                  ? lists.divisions.map((d) => ({ id: d.id, label: `${d.code}, ${d.name}` }))
                  : BENEFICIARY_KINDS.map((kind) => ({
                      id: kind,
                      label: BENEFICIARY_LABELS[kind],
                    }));

  // Account and who-benefited always need a value; the rest can be cleared.
  const clearable = field !== 'categoryId' && field !== 'beneficiary';
  const ready = value !== '' || clearable;

  return (
    <Card className="sticky top-2 z-10 border-brand-300 shadow-md">
      <CardBody className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <p className="text-sm font-medium text-content">
            {records.length} draft{records.length === 1 ? '' : 's'} selected,{' '}
            {formatMinor(total.toString())}
          </p>
          <span className="text-content-subtle">·</span>
          <label className="flex items-center gap-2 text-sm">
            <span className="text-content-muted">Set</span>
            <select
              className={select}
              value={field}
              aria-label="Field to set"
              onChange={(event) => {
                setField(event.target.value as FieldKey);
                setValue('');
              }}
            >
              {(Object.keys(FIELD_LABELS) as FieldKey[])
                .filter((key) => key !== 'vehicleId' || (lists?.vehicles.length ?? 0) > 0)
                .filter((key) => key !== 'divisionId' || (lists?.divisions.length ?? 0) > 0)
                .map((key) => (
                  <option key={key} value={key}>
                    {FIELD_LABELS[key]}
                  </option>
                ))}
            </select>
          </label>
          <label className="flex items-center gap-2 text-sm">
            <span className="text-content-muted">to</span>
            <select
              className={`${select} max-w-[18rem]`}
              value={value}
              aria-label="Value"
              onChange={(event) => setValue(event.target.value)}
            >
              <option value="">{clearable ? 'Nothing (clear it)' : 'Choose…'}</option>
              {options.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <Button
            size="sm"
            onClick={() => apply.mutate()}
            isLoading={apply.isPending}
            disabled={!ready}
          >
            Apply to {records.length}
          </Button>
          <span className="flex-1" />
          <Button
            size="sm"
            variant="secondary"
            onClick={() => confirm.mutate()}
            isLoading={confirm.isPending}
          >
            Confirm {records.length}
          </Button>
          <Button size="sm" variant="ghost" onClick={onClear}>
            Clear
          </Button>
        </div>
        {message === null ? null : <Alert tone={message.tone}>{message.text}</Alert>}
      </CardBody>
    </Card>
  );
}
